<template>
  <v-menu ref="menu" bottom offset-y :close-on-content-click="false" v-model="showAdvancedMenu">
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-bind="attrs" v-on="on"
        v-bind:class="contentClass"
        outlined dense hide-details persistent-placeholder clearable
        :label="`Search${selectedGroups.length ? (' (within ' + selectedGroups.length + ' group' + (selectedGroups.length == 1 ? '' : 's') + ')') : ''}`"
        :placeholder="advanced ? 'Advanced search' : null"
        :disabled="disabled"
        :append-icon="mdiMagnify"
        :readonly="advanced"
        :value="advanced ? '' : searchText"
        :loading="loading"
        @input="v => searchText = v"
        @click:append="advanced ? searchAdvanced() : searchBasic()"
        @click:clear="$nextTick(() => $nextTick(searchBasic))"
        @keydown.enter="searchBasic"
      />
    </template>
    <div class="pt-2 pb-3 px-2 grey lighten-4 overflow-auto" style="max-height: calc(100vh - 24px);">
      <template v-if="!hideGroups">
        <div class="pl-2 d-flex align-center">
          <div class="text--label-active">Groups</div>
          <!-- <v-spacer />
          <v-btn
            v-if="!groupContext"
            class="px-2"
            small text color="primary"
            :disabled="loadGroupsStatus.pending"
            :loading="loadGroupsStatus.pending"
            @click="isEditingGroups = !isEditingGroups"
          >
            <div class="text--label-active primary--text" style="text-transform: none;">
              {{isEditingGroups ? 'Done' : 'Edit groups'}}
            </div>
            <v-icon class="ml-2" v-html="isEditingGroups ? mdiCheck : mdiPencil" />
          </v-btn> -->
        </div>
        <v-list class="transparent mx-n2" dense>

          <v-list-item
            v-for="g in groups" :key="g.id"
            :input-value="selectedGroups.find(x => x == g.id)"
            :disabled="groupContext?.id == g.id"
            @click="toggleSearchGroup(g)"
          >
            <v-list-item-title v-text="g.name" />
            <v-list-item-icon class="ml-4 mr-1" style="height: 28px;">
              <v-icon v-if="selectedGroups.find(x => x == g.id)" v-html="mdiCheck" />
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </template>
      <div class="px-1 d-flex align-center">
        <v-switch class="mt-0" dense hide-details v-model="advanced">
          <template v-slot:label>
            <div class="text--label-active mr-3">Advanced search options</div>
          </template>
        </v-switch>
        <v-spacer />
        <v-btn v-if="!advanced && !hideGroups" small color="primary" @click="searchBasic">Search</v-btn>
      </div>
      <div v-if="advanced" class="pt-3">
        <v-form v-model="isValid">
        <div v-for="field, i in search.fields" class="d-flex align-center my-2">
          <v-btn icon small @click="search.removed.push(field.key); search.fields.splice(i, 1);">
            <v-icon v-html="mdiClose" />
          </v-btn>
          <code class="mx-2">{{field.key}}</code>
          <component
            :is="getComponent(field.type)"
            class="mt-0"
            dense hide-details
            :rules="field.type == 'pattern' ? [ isValidRe ] : []"
            v-model="field.value"
          >
            <template v-slot:append-outer>
              <v-menu bottom offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" v-blur class="px-0" text x-small>
                    <v-icon small v-html="SEARCH_FIELD_TYPES[field.type]" />
                    <v-icon small v-html="mdiMenuDown" />
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-for="icon, type in SEARCH_FIELD_TYPES" :key="type"
                    class="overline short"
                    :input-value="field.type == type"
                    :disabled="(type != 'pattern') && FIXED_FIELD_TYPES[field.key] && (FIXED_FIELD_TYPES[field.key] != type)"
                    @click="field.type = type"
                  >
                    <v-icon class="mr-2" v-html="icon" />
                    {{type}}
                    <v-spacer />
                    <v-btn
                      v-if="type == 'pattern'"
                      class="mr-n2"
                      icon
                      @click.stop="$confirm({
                        title: 'Pattern',
                        msg: `A <a href='https://docs.python.org/3/library/re.html' target='_blank'>regular expression pattern</a> is specialized text that allows for describing and searching complex conditions. Please use another search option if you're unfamiliar with regular expressions.`,
                        buttons: [ 'Ok' ],
                        primary: 'Ok'
                      })"
                    >
                      <v-icon v-html="mdiHelpCircle" />
                    </v-btn>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </component>
        </div>
        </v-form>
        <div class="d-flex align-center my-2">
          <v-menu
            right offset-x nudge-right="8" nudge-top="6" :close-on-content-click="false"
            v-model="showAddFieldMenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" v-blur icon small><v-icon v-html="mdiPlus" /></v-btn>
            </template>
            <v-card>
              <v-combobox
                v-if="showAddFieldMenu"
                outlined dense hide-details placeholder="" persistent-placeholder
                :items="Object.keys(types)"
                @input="key => {
                  search.removed = search.removed.filter(x => x == key);
                  search.fields.push({ key, type: FIXED_FIELD_TYPES[key] || 'text', value: '' });
                  showAddFieldMenu = false;
                }"
              />
            </v-card>
          </v-menu>
        </div>
        <div class="mt-5 d-flex flex-wrap align-center">
          <v-radio-group class="mt-0 pt-0" style="padding-left: 2px;" row dense hide-details v-model="advancedMode">
            <v-radio value="OR">
              <template v-slot:label><span class="text--label-active">Match any</span></template>
            </v-radio>
            <v-radio value="AND">
              <template v-slot:label><span class="text--label-active">Match all</span></template>
            </v-radio>
          </v-radio-group>
          <v-spacer />
          <v-btn small color="primary" :disabled="!isValid" @click="searchAdvanced">Search</v-btn>
        </div>
      </div>
    </div>
  </v-menu>
</template>


<style>
.group-name-input .v-input__slot {
  min-height: unset !important;
}
.group-name-input .v-input__append-inner {
  margin-top: 4px !important;
}
.group-name-input input {
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1rem;
}
</style>


<script>
import ContactFieldAddress from '@/components/ContactFieldAddress.vue';
import ContactFieldDate from '@/components/ContactFieldDate.vue';
import ContactFieldPhone from '@/components/ContactFieldPhone.vue';
import { VTextField } from 'vuetify/lib';
import { mdiClose, mdiMenuDown, mdiPlus, mdiFormatTextVariant, mdiCalendar, mdiPhone, mdiRegex, mdiMagnify, mdiMapMarker, mdiPencil, mdiDelete, mdiCheck, mdiAccountEdit, mdiHelpCircle } from '@mdi/js';


const SEARCH_FIELD_TYPES = {
  text: mdiFormatTextVariant,
  date: mdiCalendar,
  'phone number': mdiPhone,
  address: mdiMapMarker,
  pattern: mdiRegex
};


const FIXED_FIELD_TYPES = {
  name: 'text',
  email: 'text',
  phone: 'phone number',
  birthday: 'date',
  address: 'address'
};


const COMPONENTS = {
  address: ContactFieldAddress,
  date: ContactFieldDate,
  'phone number': ContactFieldPhone
};


function escapeRegex(v) {
  return v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


function translateValue(v, type) {
  switch (type) {
    case 'text':
    case 'phone number':
      // Escape any plain-text search queries
      return v && escapeRegex(v);
    case 'date':
      // Replace any unset date string components with the corresponding regex (i.e. all zeroes)
      return v && ('^' + v.split('-').map(x => /^0+$/.test(x) ? x.replace(/0/g, '\\d') : x).join('-') + '$');
    case 'address':
      return v && Object.values(JSON.parse(v)).filter(x => x).map(x => '(' + escapeRegex(x) + ')').join('.*');
    default:
      return v;
  }
}


function buildFilter({ key, value, type }) {
  const v = translateValue(value, type);
  return v && [ key, v ];
}


export default {
  name: 'contacts-search',

  props: [ 'contentClass', 'disabled', 'loading', 'types', 'groupContext', 'hideGroups' ],

  data() {
    return {
      advanced: false,
      advancedMode: 'OR',
      search: {
        fields: [
          { key: 'name', type: 'text', value: '' },
          { key: 'email', type: 'text', value: '' },
          { key: 'phone', type: 'phone number', value: '' },
          { key: 'birthday', type: 'date', value: '' }
        ],
        removed: []
      },
      isValid: true,
      showAdvancedMenu: false,
      showAddFieldMenu: false,
      searchText: '',
      groups: [],
      selectedGroups: [],
      loadGroupsStatus: this.$track('loadGroups')
    }
  },

  computed: {
    resize() {
      // We need to manually reposition the menu if its size changes to make sure it's still properly scrollable
      return [ this.groups, this.advanced, this.search ];
    }
  },

  created() {
    Object.assign(this, { FIXED_FIELD_TYPES, SEARCH_FIELD_TYPES, mdiClose, mdiMenuDown, mdiPlus, mdiMagnify, mdiPencil, mdiDelete, mdiCheck, mdiAccountEdit, mdiHelpCircle });
  },

  methods: {
    getComponent(type) {
      return COMPONENTS[type] || VTextField;
    },

    isValidRe(v) {
      try {
        new RegExp(v);
        return true;
      } catch (e) {
        return 'Invalid regular expression';
      }
    },

    searchBasic() {
      const filters = this.searchText ? [ 'name', 'email', 'phone' ].map(x => [ x, escapeRegex(this.searchText) ]) : [];
      const digits = (this.searchText || '').replace(/\D/g, '');
      if (digits.length)
        // People may enter phone numbers with formatting, we strip that and add it as a separate term here
        filters.push([ 'phone', digits ]);
      const mode = this.searchText ? 'OR' : 'AND';
      this.$emit('search', { groups: this.selectedGroups, mode, filters });
      this.showAdvancedMenu = false;
    },

    searchAdvanced() {
      if (!this.isValid)
        return;
      const filters = this.search.fields.map(buildFilter).filter(x => x);
      this.$emit('search', { groups: this.selectedGroups, mode: filters.length ? this.advancedMode : 'AND', filters });
      this.showAdvancedMenu = false;
    },

    async loadGroups() {
      const groups = await this.$call('list-contact-groups');
      this.groups = groups.map(([ id, group ]) => ({ id, name: group.name, stagedName: group.name }));
      this.groups.sort((a, b) => a.name.localeCompare(b.name));
    },

    toggleSearchGroup(group) {
      const i = this.selectedGroups.indexOf(group.id);
      if (i == -1)
        this.selectedGroups.push(group.id);
      else
        this.selectedGroups.splice(i, 1);
    },

    reload() {
      if (this.advanced)
        this.searchAdvanced();
      else
        this.searchBasic();
    }
  },

  watch: {
    globalUserId: {
      immediate: true,
      handler(v) {
        this.loadGroups();
      }
    },

    types: {
      immediate: true,
      handler(v) {
        for (const [ key, type ] of Object.entries(v || {}))
          if (!this.search.fields.find(x => x.key == key) && !this.search.removed.includes(key))
            this.search.fields.push({ key, type, value: '' });
      }
    },

    searchText(v) {
      this.group = null;
    },

    resize: {
      deep: true,
      handler() {
        this.$refs.menu.updateDimensions();
      }
    },

    selectedGroups() {
      this.reload();
    }
  }
}
</script>
